import { logEvent, store } from "../../helpers";
import { alertActions } from "../../actions";
import { loadingActions } from "../../actions";
import { sessionService } from "./session/sessionService";

const fetch = require('fetch-retry')(global.fetch);

export const api = {
  getFetch,
  postFetch,
  post,
  queryParams,
  getSession,
};

function getFetch(link, params, errorHandler) {

  const token = sessionService.sessionFromStore();
  const culture = sessionService.getCulture();

  if (!token) return Promise.reject('No token');

  let url = "";
  let buildParams = "";
  if (typeof params !== "undefined") {
    buildParams = queryParams(params);
    url = process.env.REACT_APP_API_HOST + link + "?" + buildParams;
  } else {
    url = process.env.REACT_APP_API_HOST + link;
  }

  store.dispatch(loadingActions.startloading());

  return fetch(url, {
    method: "GET",
    headers: getFetchHeaders(token, culture),
    cache: "no-store",
    retryOn: [503, 502],
    retryDelay: 2000,
    retries: 2,
  })
    .then(response => {

      store.dispatch(loadingActions.completeLoading());

      if (response.ok) return readOkResponse(response);

      if(response.status === 503 || response.status === 502) {
        return Promise.reject(get503StatusErrors(link))
      }

      if (typeof (errorHandler) === "function")
        return errorHandler(response);

      if (response.status === 403 || response.status === 401) {
        const errors = [{
          errorCode: 403,
          message: 'Your session has expired.'
        }]          
        return Promise.reject(errors)
      } 
      else if(response.status === 404) {
        const errors = [{
          errorCode: 404,
          message: "The requested resource does not exist."
        }]

        logEvent({
          trackId: 'notracking',
          eventName: 'ajax error',
          details: {
            request: `GET ${link}`,
            params,
            errors
          }
        })
        return Promise.reject(errors)
      }

      return parseErrorResponse(response).then(errors => Promise.reject(errors));
    })
    .catch(error => {
      if (Array.isArray(error)) {
        store.dispatch(alertActions.error(error))
        return Promise.reject(error);
      }
      store.dispatch(loadingActions.completeLoading());
      if (error) {
        error = [{
          errorCode: 500,
          message: getErrorMessage(error)
        }];
        store.dispatch(alertActions.error(error));

        logEvent({
          trackId: 'notracking',
          eventName: 'ajax error',
          details: {
            request: `GET ${link}`,
            params,
            errors: error
          }
        })
      }
      return Promise.reject(error);
    });
}

function postFetch(link, params, errorHandler) {

  const token = sessionService.sessionFromStore();
  const culture = sessionService.getCulture();

  if (!token) return Promise.reject('No token');

  store.dispatch(loadingActions.startloading());

  return fetch(process.env.REACT_APP_API_HOST + link, {
    method: "POST",
    headers: getFetchHeaders(token, culture),
    body: JSON.stringify(params),
    // POSTs are non-idempotent (payments, bookings, mutations) — a 502/503
    // doesn't guarantee the origin didn't process the request, so retrying
    // risks double-charging/double-mutating. No auto-retry here; see getFetch
    // for GETs and getSession, which are safe to retry.
  })
    .then(response => {
      store.dispatch(loadingActions.completeLoading());
      if (response.ok) return readOkResponse(response);

      if(response.status === 503 || response.status === 502) {
        return Promise.reject(get503StatusErrors(link))
      }

      // error happened
      if (typeof (errorHandler) === "function")
        return errorHandler(response);

      if (response.status === 403 || response.status === 401) {
        const errors = [{
          errorCode: 403,
          message: 'Your session has expired.'
        }]
        return Promise.reject(errors)
      }
      else if(response.status === 404) {
        const errors = [{
          errorCode: 404,
          message: "The requested resource does not exist."
        }]

        logEvent({
          trackId: 'notracking',
          eventName: 'ajax error',
          details: {
            request: `POST ${link}`,
            params,
            errors
          }
        })
        return Promise.reject(errors)
      }

      return parseErrorResponse(response).then(errors => Promise.reject(errors));
    })
    .catch(error => {
      if (Array.isArray(error)) {
        store.dispatch(alertActions.error(error));
        return Promise.reject(error);
      }

      store.dispatch(loadingActions.completeLoading());
      if (error) {
        error = [{
          errorCode: 500,
          message: getErrorMessage(error)
        }];
        store.dispatch(alertActions.error(error));

        logEvent({
          trackId: 'notracking',
          eventName: 'ajax error',
          details: {
            request: `POST ${link}`,
            params,
            errors: error
          }
        })
      }
      return Promise.reject(error);
    });
}

// post without loading status
// used for logging errors to the server
function post(url, params) {

  const token = sessionService.sessionFromStore();
  const culture = sessionService.getCulture();

  if (!token) return Promise.reject('No token');

  return fetch(process.env.REACT_APP_API_HOST + url, {
    method: "POST",
    headers: getFetchHeaders(token, culture),
    body: JSON.stringify(params),
    retries: 0, // no retry for logging errors
  })
    .then(response => {
      if (response.ok) return readOkResponse(response);

      if(response.status === 503 || response.status === 502) {
        return Promise.reject(get503StatusErrors(url))
      }

      if (response.status === 403 || response.status === 401) {
        const errors = [{
          errorCode: 403,
          message: 'Your session has expired.'
        }]
        return Promise.reject(errors)
      }

      return parseErrorResponse(response).then(errors => Promise.reject(errors));
    })
    .catch(error => {
      if (Array.isArray(error)) return Promise.reject(error);
      if (error) {
        error = [{
          errorCode: 500,
          message: getErrorMessage(error)
        }];
      }
      return Promise.reject(error);
    });
}

async function getSession(link) {

  store.dispatch(loadingActions.startloading());

  try {
    const url = process.env.REACT_APP_API_HOST + link;
    const response = await fetch(url, {
      method: "POST",
      // session creation is safe to repeat, so retry all transient statuses
      retryOn: [500, 502, 503, 504, 429],
      retryDelay: 2000,
      retries: 2,
    });

    store.dispatch(loadingActions.completeLoading());

    if (response.ok) {
      const token = response.headers.get("X-Session-Token");
      const version = response.headers.get("X-Api-Version");
      if (!token) return Promise.reject(new Error("No session token in response"));
      return { token, version };
    }

    return Promise.reject(new Error(`Session request failed: ${response.status}`));
  }
  catch (error) {
    store.dispatch(loadingActions.completeLoading());
    return Promise.reject(error);
  }
}

function queryParams(params) {
  return Object.keys(params)
    .map(k => {
      if (Array.isArray(params[k])) {
        return params[k]
          .map(val => encodeURIComponent(k) + "=" + encodeURIComponent(val))
          .join("&");
      }
      return encodeURIComponent(k) + "=" + encodeURIComponent(params[k]);
    })
    .join("&");
}

function getResponseStatusError(response) {
  if (!response || !response.status) {
    return [{
      errorCode: "unknow",
      message: "Unknow error"
    }];
  }
  else {
    return [{
      errorCode: response.status,
      message: response.status >= 500 ? "An error occurred. Please try again later." : "An error occurred with your request. Please check and try again."
    }];
  }
}

function readOkResponse(response) {
  const newToken = response.headers.get("X-Session-Token");
  if (newToken) sessionService.setSessionToStore(newToken);

  const contentType = response.headers.get('Content-Type');
  if (contentType && contentType.indexOf('application/json') > -1) return response.json();
  if (contentType && contentType.indexOf('application/pdf') > -1) return response.blob();
  if (contentType && contentType.indexOf('application/vnd.apple.pkpass') > -1) return response.blob();
  return response.text();
}

function getFetchHeaders(token, culture) {
  return {
    "Accept": "application/json",
    "Content-Type": "application/json",
    "X-Session-Token": token,
    "Culture": culture,
    "cache-control": "no-store"
  }
}

function get503StatusErrors(link = '') {
  let errors = [];
  if (link.includes('/payment/')) {
    errors.push({
      errorCode: 400,
      message: "Sorry, we can't process your payment at the moment, please try again later."
    })
  }
  else {
    errors.push({
      errorCode: 503,
      message: "Cannot connect at the moment. Check your internet or try again shortly."
    })
  }
  return errors
}

function parseErrorResponse(response) {
  return response.text().then(text => {
    try {
      const data = JSON.parse(text);
      return Array.isArray(data) ? data : getResponseStatusError(response);
    } catch {
      return getResponseStatusError(response);
    }
  });
}

function getErrorMessage(error) {
  if(error?.message === "Failed to fetch") {
    return "Cannot connect at the moment. Check your internet or try again shortly.";
  }

  return error?.message ?? error?.TypeError ?? "An error occurred. Please try again later.";
}